import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Box, Button, Stack, Tooltip, IconButton, Switch, useToast, useDisclosure, Skeleton } from "@chakra-ui/react";
import { Filter } from "lucide-react";
import { Trash } from "lucide-react";
import { PlusCircle } from "lucide-react";

import OrganizationService from "@/services/organizationService";
import { DataTable } from "@/utils/data-table";

import CreateMIDModal from "./CreateMIDModal";

export function MIDTable() {
  const toast = useToast();
  const [processingMids, setProcessingMids] = useState({});

  const [subOrgs, setSubOrgs] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const { orgDetails = {}, userDetails = {} } = useSelector((state) => state?.login) || {};
  const canCreateMID = userDetails?.permissions?.includes("mid.create");
  const canUpdateeMID = userDetails?.permissions?.includes("mid.update");
  const showToast = useCallback((description, status) => {
    toast({
      title: status === "error" ? "Error" : "Success",
      description: description || "Something went wrong",
      status: status || "success",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
  }, []);

  const fetchSubOrgs = useCallback(async () => {
    try {
      const response = await OrganizationService.getSubOrgs();
      const customerAndSubOrgs = response?.data?.data?.data ?? [];
      const subOrgs = customerAndSubOrgs;
      setSubOrgs(subOrgs.sort((a, b) => a.formattedName.localeCompare(b.formattedName)));
    } catch (error) {
      showToast(error?.data?.message || error?.message || "Failed to fetch sub-organizations", "error");
    }
  }, [orgDetails.orgId, showToast]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchSubOrgs();
      setLoading(false);
    };
    fetchData();
  }, [fetchSubOrgs]);

  const disableMIDhandler = async ({ mid }) => {
    try {
      setProcessingMids((prev) => ({ ...prev, [mid]: true }));
      const response = await OrganizationService.toggleMid({ mid });
      await fetchSubOrgs();
      showToast(response?.data?.message || "MID disabled successfully", "success");
    } catch (error) {
      console.error("Error disabling MID:", error);
      const errMsg = error?.data?.message || error?.message || "Error disabling MID";
      showToast(errMsg, "error");
    } finally {
      setProcessingMids((prev) => ({ ...prev, [mid]: false }));
    }
  };

  const columns = [
    { accessorKey: "name", header: "Name" },
    {
      accessorKey: "merchant",
      header: "Merchant",
      cell: ({ row }) => {
        const parts = row.original?.mid?.split("_") || [];
        const merchant = parts.length > 1 ? parts[1] : "NA";

        return <div className="flex items-center gap-2 pr-3 py-3">{merchant}</div>;
      },
    },
    {
      accessorKey: "subMerchant",
      header: "Sub Merchant",
      cell: ({ row }) => {
        const parts = row.original?.mid?.split("_") || [];
        const subMerchant = parts.length > 2 ? parts[2] : "NA";

        return <div className="flex items-center gap-2 pr-3 py-3">{subMerchant}</div>;
      },
    },
    { accessorKey: "mid", header: "MID" },
    {
      accessorKey: "midEnabled",
      header: "MID Enabled",
      cell: ({ row }) => {
        const midEnabled = row.original?.midEnabled || false;
        const mid = row.original?.mid || "";
        return (
          <div className="flex items-center gap-2 pr-3">
            {canUpdateeMID ? (
              <Switch
                isChecked={midEnabled}
                isDisabled={processingMids[mid]}
                className="pr-3"
                onChange={(e) => {
                  disableMIDhandler({ mid });
                }}
              />
            ) : (
              <span className="text-gray-500">{midEnabled ? "Enabled" : "Disabled"}</span>
            )}
          </div>
        );
      },
    },
    // {
    //   id: "actions",
    //   header: "",
    //   cell: ({ row }) => {
    //     const product = row.original;
    //     return (
    //       <div className="flex justify-end items-center gap-2 pr-3">
    //         <Tooltip label="Delete" hasArrow>
    //           <IconButton
    //             aria-label="Delete"
    //             icon={<Trash size={16} />}
    //             size="sm"
    //             variant="ghost"
    //             colorScheme="red"
    //             textColor={"red.600"}
    //             backgroundColor={"#FEE2E2"}
    //             onClick={() => console.log("Delete clicked for", product)}
    //           />
    //         </Tooltip>
    //       </div>
    //     );
    //   },
    // },
  ];

  if (loading) {
    return (
      <Box bg="white" w="100%" borderRadius={"9px"} p={"20px 40px"}>
        <Stack borderRadius={"9px"} mt={"15px"} rowGap={5} bg={"white"}>
          {Array(6)
            .fill(0)
            .map((_, index) => (
              <Skeleton key={index} height="60px" />
            ))}
        </Stack>
      </Box>
    );
  }

  return (
    <div className="py-5">
      {loading ? (
        <Box bg="white" w="100%" borderRadius={"9px"} p={"20px 40px"}>
          <Stack borderRadius={"9px"} mt={"15px"} rowGap={5} bg={"white"}>
            {Array(6)
              .fill(0)
              .map((_, index) => (
                <Skeleton key={index} height="60px" />
              ))}
          </Stack>
        </Box>
      ) : (
        <>
          {canCreateMID && (
            <Box w="100%" py={4} textAlign="right">
              <Stack direction="row" spacing={2} justify="flex-end">
                <Button bgColor={"#1A4FD6"} textColor={"white"} _hover={{ bgColor: "#163FB3" }} leftIcon={<PlusCircle size={15} />} size="sm" onClick={onOpen}>
                  Create MID
                </Button>
              </Stack>
            </Box>
          )}
          <DataTable columns={columns} data={subOrgs} />
        </>
      )}
      <CreateMIDModal isOpen={isOpen} onClose={onClose} refreshData={fetchSubOrgs} setIsMidLoading={setLoading} subOrgs={subOrgs} showToast={showToast} />
    </div>
  );
}
